import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  PowerSettingsNew as DeactivateIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useApiQuery } from '../hooks/useApiQuery';
import { roomsApi } from '../services/roomsService';
import { SchulungsraumDefinition } from '../services/types';
import { TableSkeleton } from '../components/SkeletonLoaders/TableSkeleton';
import ErrorMessage from '../components/ErrorMessage';
import { RoomEditorDialog, RoomFormValues } from '../components/RoomEditorDialog';

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error';
}

export default function RoomsPage() {
  const { hasRole } = useAuth();
  const isAdmin = hasRole('admin');

  // NC-1270: the list is readable by every authenticated role (rooms-list is
  // viewer+), so it loads unconditionally. Only the mutations below stay
  // admin-only.
  const { data: rooms, loading, error, refetch } = useApiQuery<SchulungsraumDefinition[]>(
    () => roomsApi.list(),
    []
  );

  const [editorRoom, setEditorRoom] = useState<SchulungsraumDefinition | null | undefined>(
    undefined
  );
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success',
  });

  const closeSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));
  const openCreate = () => setEditorRoom(null);
  const openEdit = (room: SchulungsraumDefinition) => setEditorRoom(room);
  const closeEditor = () => setEditorRoom(undefined);

  const handleSubmit = async (values: RoomFormValues) => {
    setSaving(true);
    try {
      if (editorRoom === null) {
        // Create mode
        await roomsApi.create(values);
        setSnackbar({ open: true, message: `Raum "${values.name}" wurde angelegt.`, severity: 'success' });
      } else if (editorRoom !== undefined) {
        // Edit mode
        await roomsApi.update(editorRoom.id, values);
        setSnackbar({ open: true, message: `Raum "${values.name}" wurde aktualisiert.`, severity: 'success' });
      }
      closeEditor();
      await refetch();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unbekannter Fehler.';
      setSnackbar({ open: true, message: `Fehler: ${message}`, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (room: SchulungsraumDefinition) => {
    try {
      await roomsApi.delete(room.id);
      setSnackbar({ open: true, message: `Raum "${room.name}" wurde deaktiviert.`, severity: 'success' });
      await refetch();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unbekannter Fehler.';
      setSnackbar({ open: true, message: `Fehler: ${message}`, severity: 'error' });
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4">Schulungsraeume</Typography>
          <Typography variant="body2" color="text.secondary">
            {isAdmin
              ? 'Verwaltung aller Schulungsraeume inkl. inaktiver Eintraege.'
              : 'Uebersicht aller Schulungsraeume inkl. inaktiver Eintraege. Anlegen und Bearbeiten ist Admins vorbehalten.'}
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          {isAdmin && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={openCreate}
              disabled={loading}
            >
              Raum anlegen
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={refetch}
            disabled={loading}
          >
            Aktualisieren
          </Button>
        </Box>
      </Box>

      {loading && <TableSkeleton rows={4} columns={5} />}

      {!loading && error && (
        <ErrorMessage
          message={error}
          title="Schulungsraeume konnten nicht geladen werden"
          onRetry={refetch}
        />
      )}

      {!loading && !error && rooms && rooms.length === 0 && (
        <Alert severity="info">
          {isAdmin ? (
            <>
              Noch keine Schulungsraeume angelegt. Klicken Sie auf{' '}
              <strong>Raum anlegen</strong>, um den ersten Raum zu erstellen.
            </>
          ) : (
            <>Noch keine Schulungsraeume angelegt. Ein Admin kann Raeume hinterlegen.</>
          )}
        </Alert>
      )}

      {!loading && !error && rooms && rooms.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small" aria-label="Schulungsraeume-Tabelle">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Standort</TableCell>
                <TableCell>Arbeitsplaetze</TableCell>
                <TableCell>Status</TableCell>
                {isAdmin && <TableCell align="right">Aktionen</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {rooms.map((room) => (
                <TableRow key={room.id} hover>
                  <TableCell>{room.name}</TableCell>
                  <TableCell>{room.location}</TableCell>
                  <TableCell>
                    {room.capacity}
                    {/* NC-1403: annotated seats (tutor-visible note + Matrix42 id) */}
                    {(room.workstations ?? []).map((w) => (
                      <Typography key={w.seat} variant="caption" color="text.secondary" component="div">
                        Platz {w.seat}
                        {w.note ? `: ${w.note}` : ''}
                        {w.assetRef ? ` (Matrix42: ${w.assetRef})` : ''}
                      </Typography>
                    ))}
                  </TableCell>
                  <TableCell>
                    {!room.isActive && (
                      <Chip label="Inaktiv" size="small" />
                    )}
                  </TableCell>
                  {isAdmin && (
                    <TableCell align="right">
                      <Tooltip title={`${room.name} bearbeiten`}>
                        <IconButton
                          size="small"
                          aria-label={`${room.name} bearbeiten`}
                          onClick={() => openEdit(room)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {room.isActive && (
                        <Tooltip title={`${room.name} deaktivieren`}>
                          <IconButton
                            size="small"
                            aria-label={`${room.name} deaktivieren`}
                            onClick={() => handleDeactivate(room)}
                          >
                            <DeactivateIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <RoomEditorDialog
        open={editorRoom !== undefined}
        room={editorRoom ?? null}
        saving={saving}
        onSubmit={handleSubmit}
        onClose={closeEditor}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
